/**
 * useChatSSE Hook
 *
 * Server-Sent Events (SSE) based real-time chat message receiver
 * Falls back to polling if SSE is not available or fails
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";

interface Message {
  id: string;
  chat_id: string;
  sender_id: string;
  body: string;
  has_masked_link: boolean;
  attachments_json?: any[];
  created_at: number;
}

interface UseChatSSEOptions {
  chatId: string;
  enabled?: boolean;
  onNewMessage?: (message: Message) => void;
  onConnectionChange?: (connected: boolean) => void;
}

const API_BASE = import.meta.env.VITE_API_BASE || "";

export function useChatSSE({
  chatId,
  enabled = true,
  onNewMessage,
  onConnectionChange,
}: UseChatSSEOptions) {
  const { user } = useAuth();
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000; // 3 seconds

  const connect = useCallback(async () => {
    if (!enabled || !chatId || !user) return;

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // Don't connect if tab is hidden (fallback to polling)
    if (document.hidden) {
      setConnected(false);
      onConnectionChange?.(false);
      return;
    }

    try {
      // Get Firebase auth token
      const token = await user.getIdToken();

      // Build SSE URL with token as query parameter
      // Note: EventSource doesn't support custom headers, so we pass token as query param
      const url = `${API_BASE}/api/chat/stream?chat_id=${chatId}&token=${encodeURIComponent(token)}`;

      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log("[SSE] Connected to chat stream");
        setConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
        onConnectionChange?.(true);
      };

      eventSource.onmessage = (event) => {
        // Handle keepalive messages (no event type)
        if (event.data === "keepalive" || !event.data) {
          return;
        }

        try {
          const data = JSON.parse(event.data);
          if (data.id && data.chat_id) {
            // This is a message event
            onNewMessage?.(data as Message);
          }
        } catch (err) {
          console.error("[SSE] Error parsing message:", err);
        }
      };

      eventSource.addEventListener("message", (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          if (data.id && data.chat_id) {
            onNewMessage?.(data as Message);
          }
        } catch (err) {
          console.error("[SSE] Error parsing message event:", err);
        }
      });

      eventSource.onerror = (err) => {
        console.error("[SSE] Connection error:", err);
        setConnected(false);
        onConnectionChange?.(false);

        // Close and attempt reconnect
        eventSource.close();
        eventSourceRef.current = null;

        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          const delay = reconnectDelay * reconnectAttemptsRef.current;
          console.log(`[SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else {
          console.error("[SSE] Max reconnect attempts reached");
          setError("연결에 실패했습니다. 폴링 모드로 전환합니다.");
        }
      };
    } catch (err) {
      console.error("[SSE] Failed to connect:", err);
      setError(err instanceof Error ? err.message : "연결 실패");
      setConnected(false);
      onConnectionChange?.(false);
    }
  }, [chatId, enabled, user, onNewMessage, onConnectionChange]);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setConnected(false);
    onConnectionChange?.(false);
  }, [onConnectionChange]);

  // Handle visibility change (tab visibility)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden: disconnect SSE, fallback to polling
        disconnect();
      } else if (enabled && chatId && user) {
        // Tab visible: reconnect SSE
        connect();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, chatId, user, connect, disconnect]);

  // Connect on mount or when dependencies change
  useEffect(() => {
    if (enabled && chatId && user && !document.hidden) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, chatId, user, connect, disconnect]);

  return {
    connected,
    error,
    disconnect,
    reconnect: connect,
  };
}

